import { BasicDialogWindow, Dialog, DialogButton } from '../../../../widgets/dialog';
import { FormControl } from '../../../../widgets/formControl';
import { Input } from '../../../../widgets/input';
import React, { FC, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { ModelColor } from '../../state/model/modelColor';
import { Side } from '../../state/side';

interface JSONSettingsDialogProps {
    modelColor: ModelColor;
    sides: Side[];
    onClose: () => void;
    onSave: (newModelColor: { settings: { colorProviderCode: string | null; sideCodes: { [sideGuid: string]: string } } }) => void;
}

const StyledFormControl = styled(FormControl)`
    margin-bottom: 20px;
`;

const SideCodeSection = styled.div`
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
`;

const SideCodeTitle = styled.h4`
    margin-bottom: 15px;
    color: #333;
    font-size: 14px;
    font-weight: 600;
`;

const JSONSettingsDialog: FC<JSONSettingsDialogProps> = ({ modelColor, sides, onClose, onSave }) => {
    const [colorProviderCode, setColorProviderCode] = useState<string>(modelColor.colorProviderCode || '');
    const [sideCodes, setSideCodes] = useState<{ [sideGuid: string]: string }>(() => {
        const initialSideCodes: { [sideGuid: string]: string } = {};
        modelColor.sides.forEach((side) => {
            initialSideCodes[side.guid] = side.code || '';
        });
        return initialSideCodes;
    });

    const handleSideCodeChange = (sideGuid: string, value: string) => {
        setSideCodes((prev) => ({
            ...prev,
            [sideGuid]: value
        }));
    };

    const handleSave = () => {
        const trimmedSideCodes: { [sideGuid: string]: string } = {};
        Object.keys(sideCodes).forEach((sideGuid) => {
            trimmedSideCodes[sideGuid] = sideCodes[sideGuid].trim();
        });

        onSave({
            settings: {
                colorProviderCode: colorProviderCode.trim() === '' ? null : colorProviderCode.trim(),
                sideCodes: trimmedSideCodes
            }
        });
    };

    const buttons: DialogButton[] = [
        {
            label: T._('Cancel', 'Admin'),
            outline: true,
            onClick: onClose
        },
        {
            label: T._('Save', 'Admin'),
            outline: false,
            onClick: handleSave
        }
    ];

    return (
        <Dialog windowDecorator={BasicDialogWindow} title={T._('JSON Settings', 'Admin')} buttons={buttons}>
            <StyledFormControl>
                <label>{T._('Color Provider Code', 'Admin')}</label>
                <Input
                    type='text'
                    value={colorProviderCode}
                    onChange={(e) => setColorProviderCode(e.target.value)}
                    placeholder={T._('Enter color provider code', 'Admin')}
                />
            </StyledFormControl>

            <SideCodeSection>
                <SideCodeTitle>{T._('Side Codes', 'Admin')}</SideCodeTitle>
                {modelColor.sides.map((side, index) => {
                    const originalSide = sides.find((s) => s.guid === side.originalSideGuid);
                    const sideName = originalSide ? originalSide.name : `Side ${index + 1}`;
                    return (
                        <StyledFormControl key={side.guid}>
                            <label>{sideName}</label>
                            <Input
                                type='text'
                                value={sideCodes[side.guid] || ''}
                                onChange={(e) => handleSideCodeChange(side.guid, e.target.value)}
                                placeholder={T._('Enter side code', 'Admin')}
                            />
                        </StyledFormControl>
                    );
                })}
            </SideCodeSection>
        </Dialog>
    );
};

export default JSONSettingsDialog;
